import { Ability } from '@casl/ability';
import { getTimeWithOffset } from '@dispatch/shared';
import {
  Actions,
  ITaskMessage,
  ResultStatus,
  Subjects,
  TaskRescheduleRequest,
  TaskType,
} from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import {
  checkboxSelectionColumn,
  dateColumn,
  tableStyleHideSeparator,
  tableStyleOverrides,
  textColumn,
  MUI_X_5_GRID_PROPS,
  useGridApiRefKeepingPage,
} from '@dispatch/web/ui';
import { Stack } from '@mui/material';
import {
  GridColDef,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import type { GridInitialState } from '@mui/x-data-grid-pro';
import { addMinutes, formatISO } from 'date-fns';
import React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useParams } from 'react-router';
import Button from '@mui/material/Button';
import { ResourceContext } from '../ResourcesDetails';
import { isTaskReassignable } from '@dispatch/shared/services';

// this function will work as long as we keep the taskType naming convention XXX_YYY
// creates a friendly name in the format for Xxx Yyy
const mapTaskTypeFriendlyName = (taskType: string) => {
  const splitString = taskType.split('_');

  return splitString
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const getGridColumns = (showAppointment: boolean, resourceContext: any) => {
  const columns: GridColDef[] = [
    textColumn({
      field: 'claimNumber',
      headerName: 'Claim Number',
      flex: 2,
    }),
    textColumn({
      field: 'insuredName',
      headerName: 'Name',
      flex: 2,
    }),
    textColumn({
      field: 'lossState',
      headerName: 'State',
    }),
    textColumn({
      field: 'lossCity',
      headerName: 'City',
    }),
    textColumn({
      field: 'taskType',
      headerName: 'Task Type',
      valueGetter: (value) => (value ? mapTaskTypeFriendlyName(value) : ''),
    }),
  ];

  if (showAppointment) {
    columns.push(
      dateColumn(
        {
          field: 'date',
          headerName: 'Date',
          valueGetter: (_value, row) =>
            !row.appointment
              ? '-'
              : getTimeWithOffset(row.timeZoneId, row.appointment),
        },
        'MMM dd, yyyy , hh:mm a'
      )
    );
  } else {
    columns.push(
      dateColumn(
        {
          field: 'mustStartAfter',
          headerName: 'Must Start After',
          // flex: 2,
          valueGetter: (_value, row) =>
            getTimeWithOffset(row.timeZoneId, row.mustStartAfter),
        },
        'MMM dd, yyyy | HH:mm'
      ),
      dateColumn(
        {
          field: 'mustEndBefore',
          headerName: 'Must End Before',
          // flex: 2,
          valueGetter: (_value, row) =>
            getTimeWithOffset(row.timeZoneId, row.mustEndBefore),
        },
        'MMM dd, yyyy | HH:mm'
      ),
      {
        field: 'action',
        headerName: '...',
        sortable: false,
        renderCell: ({ row }: Partial<GridRowParams>) => {
          return (
            isTaskReassignable(row) && (
              <Button
                data-testid="re-assign-button"
                onClick={() => {
                  resourceContext?.resourceMeta?.setTaskToAssign(row);
                }}
              >
                Re-Assign
              </Button>
            )
          );
        },
      }
    );
  }

  return columns;
};
/**
 *
 * Tasks returns a data grid that is specific for the taks tab for the resource view.
 * Dimensions and cofig are a bit different than our global reusable table.
 */
export const Tasks = ({
  taskType,
  height,
  setSelected,
  width = '100%',
  data,
  permissions,
  showAppointment = false,
  resourceFetchStatus,
}: {
  taskType: string;
  height: string;
  width?: string;
  setSelected?: UseFormSetValue<{
    selected: TaskRescheduleRequest[];
    selectedAssignedTasks: ITaskMessage[];
  }>;
  getSelected?: TaskRescheduleRequest[];
  data: { data: ITaskMessage[] | undefined; loading: boolean };
  permissions: Ability<[Actions, Subjects]> | undefined;
  showAppointment?: boolean;
  resourceFetchStatus?: ResultStatus;
}) => {
  const apiRef = useGridApiRefKeepingPage();
  const params = useParams();
  const [selectedTasks, setSelectedTasks] =
    React.useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
  const resourceContext = React.useContext(ResourceContext);

  const pagination = {
    pagingOptions: [5, 10, 15, 20],
    pageSize: 10,
  };

  const initialState: GridInitialState = {
    pagination: { paginationModel: { pageSize: pagination.pageSize } },
  };

  const taskLookup: Record<string, ITaskMessage> | undefined =
    React.useMemo(() => {
      return data?.data?.reduce(function (
        previousValue: Record<string, ITaskMessage>,
        currentValue: ITaskMessage & { id?: string }
      ) {
        previousValue[currentValue.id || ''] = currentValue;
        return previousValue;
      },
      {});
    }, [data?.data]);

  const mapTasks = (selection: GridRowSelectionModel) => {
    return [...selection.ids].flatMap(
      (id: GridRowId): TaskRescheduleRequest | [] => {
        const task: ITaskMessage | undefined = taskLookup?.[id];

        // if task is not found in lookup, then return empty array for flatMap to clean up
        if (!task || !task.appointment) return [];
        return {
          id: id as string,
          resourceId: params['resourceId'] || '',
          reasonCode: 'other',
          appointmentStart: formatISO(new Date(task.appointment)),
          appointmentEnd: formatISO(
            addMinutes(
              new Date(task?.appointment),
              task?.appointmentDurationInMinutes || 30 // using 30 to prevent 0 duration schedules. This is what is set in scheduler as well
            )
          ),
        };
      }
    );
  };

  const mapData = (selection: GridRowSelectionModel) => {
    setSelectedTasks(selection);
    if (taskType == 'unassigned') {
      setSelected &&
        setSelected('selected', mapTasks(selection), { shouldDirty: true });
    } else {
      setSelected &&
        setSelected(
          'selectedAssignedTasks',
          data.data?.filter(
            (x) => x.id != undefined && selection.ids.has(x.id)
          ) || [],
          { shouldDirty: true }
        );
    }
  };

  return (
    <div style={{ height, width }}>
      <DataGridPro
        apiRef={apiRef}
        {...MUI_X_5_GRID_PROPS}
        loading={resourceFetchStatus === 'loading'}
        disableVirtualization={isRunningInJest()}
        onRowSelectionModelChange={mapData}
        rows={data.data || []}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No Tasks
            </Stack>
          ),
          noResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              Filter Returned No Tasks
            </Stack>
          ),
        }}
        rowSelectionModel={selectedTasks}
        disableRowSelectionExcludeModel
        rowHeight={32}
        columns={[
          checkboxSelectionColumn,
          ...getGridColumns(showAppointment, resourceContext),
        ]}
        pagination={true}
        initialState={initialState}
        pageSizeOptions={pagination.pagingOptions}
        checkboxSelection={permissions?.can?.('upsert', 'resource')}
        sx={[tableStyleHideSeparator, tableStyleOverrides]}
      />
    </div>
  );
};
